import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Sparkles, Zap, Crown, Star, FileSignature, Calendar } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useSchedulingFeature } from '../context/SchedulingFeatureContext';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PricingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, user } = useAuth();
  const { isDark } = useTheme();
  const { trackUpgradeStarted, trackUpgradeCompleted } = useSchedulingFeature();
  const { toast } = useToast();
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  
  // Check if coming from scheduling upgrade flow
  const highlightPlan = searchParams.get('highlight');
  const highlightFeature = searchParams.get('feature');

  useEffect(() => {
    setLoading(false);
    if (token) {
      fetchCurrentSubscription();
    }
  }, [token]);

  const fetchCurrentSubscription = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/subscriptions/my-subscription`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentSubscription(response.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  const handleSubscribe = async (planId) => {
    if (!token) {
      toast({
        variant: 'destructive',
        title: 'Login Required',
        description: 'Please login to subscribe',
      });
      navigate('/login');
      return;
    }

    // Track if this is a scheduling upgrade
    if (highlightFeature === 'scheduling' && planId === 'on_ya_royalties') {
      trackUpgradeStarted();
    }

    setSubscribing(true);
    try {
      // For The Contract tier, use subscription API (free tier)
      if (planId === 'contract') {
        const response = await axios.post(
          `${API_URL}/api/subscriptions/subscribe/contract`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        toast({
          title: 'Welcome to The Contract!',
          description: 'You now have access to all Premium features. 20% of your sales will go to the admin.',
        });
        
        await fetchCurrentSubscription();
        setTimeout(() => navigate('/browse'), 1500);
        setSubscribing(false);
        return;
      }

      // For paid plans, redirect to Stripe checkout
      const response = await axios.post(
        `${API_URL}/api/payments/checkout/session`,
        {
          package_id: planId,
          origin_url: window.location.origin,
          currency: 'usd',
          metadata: highlightFeature === 'scheduling' ? { upgrade_reason: 'scheduling' } : {}
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Redirect to Stripe Checkout
      window.location.href = response.data.url;
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to start checkout',
      });
      setSubscribing(false);
    }
  };

  const plans = [
    {
      id: 'basic',
      name: 'Basic Plan',
      price: 0,
      icon: <Zap className="w-8 h-8 text-blue-400" />,
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'from-blue-500/10 to-cyan-500/10',
      borderColor: 'border-blue-500/50',
      features: [
        'View all content posted',
        'Browse full content library',
        'Follow creators',
        'Like and share content',
        'Basic messaging'
      ],
      popular: false,
      isContract: false,
      isFree: true,
      canPost: false,
      note: 'View only - cannot upload content'
    },
    {
      id: 'on_ya_royalties',
      name: 'Own Ya Royalties',
      price: 30.00,
      period: 'year',
      monthlyEquivalent: '2.50',
      icon: <Star className="w-8 h-8 text-yellow-400" />,
      color: 'from-yellow-400 to-orange-500',
      bgColor: 'from-yellow-500/10 to-orange-500/10',
      borderColor: 'border-yellow-500/50',
      features: [
        'Upload unlimited content',
        'Keep 100% of your earnings',
        'Post scheduling (auto-publish)',
        'Advanced creator analytics',
        'Priority support',
        'Creator verification badge'
      ],
      popular: true,
      isContract: false,
      canPost: true,
      includesScheduling: true,
      note: 'Established creators who want full control'
    },
    {
      id: 'contract',
      name: 'The Contract',
      price: 0,
      priceLabel: 'FREE',
      icon: <FileSignature className="w-8 h-8 text-emerald-400" />,
      color: 'from-emerald-400 to-cyan-500',
      bgColor: 'from-emerald-500/10 to-cyan-500/10',
      borderColor: 'border-emerald-500/50',
      description: 'Free forever. Admin receives 20% of your sales automatically.',
      features: [
        'All Premium features included',
        'Upload unlimited content',
        'Post videos, photos, audio',
        'Advanced analytics',
        'Priority support',
        'Creator dashboard',
        'Custom branding options',
        '24/7 priority support'
      ],
      popular: false,
      isContract: true,
      canPost: true,
      commissionNote: '20% of your sales go to the platform'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black flex items-center justify-center">
        <div className="text-white">Loading plans...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-purple-900/20 to-black py-16 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-black bg-gradient-to-r from-red-500 via-yellow-500 via-green-500 via-blue-500 to-purple-500 bg-clip-text text-transparent mb-4">
            Choose Your Plan
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Support Black creators and get access to exclusive content
          </p>
          {currentSubscription && (
            <div className="mt-6">
              <Badge className="bg-gradient-to-r from-yellow-400 to-purple-500 text-black px-4 py-2 text-sm">
                Current Plan: {currentSubscription.plan?.name || 'Free'}
              </Badge>
            </div>
          )}
          <div className="mt-6 inline-flex items-center gap-2 bg-green-500/10 border border-green-500/20 rounded-full px-4 py-2">
            <Check className="w-5 h-5 text-green-400" />
            <span className="text-green-400 text-sm font-semibold">
              All plans support multi-currency payments
            </span>
          </div>
        </div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {plans.map((plan) => {
            const isHighlighted = highlightPlan === plan.id;
            
            return (
            <Card
              key={plan.id}
              className={`relative bg-gradient-to-br ${plan.bgColor} border-2 ${
                isHighlighted ? 'border-yellow-400 shadow-2xl shadow-yellow-400/40 ring-2 ring-yellow-400/50 scale-105' :
                plan.popular ? 'border-yellow-500 shadow-xl shadow-yellow-500/20' : 
                plan.isContract ? 'border-emerald-500 shadow-xl shadow-emerald-500/20' : plan.borderColor
              } overflow-hidden transition-transform hover:scale-105`}
              role="article"
              aria-label={`${plan.name} pricing plan${isHighlighted ? ' - Recommended for you' : ''}`}
            >
              {isHighlighted && highlightFeature === 'scheduling' && (
                <div 
                  className="absolute top-0 left-0 right-0 bg-gradient-to-r from-yellow-400 to-orange-500 text-black text-xs font-bold px-3 py-2 text-center"
                  role="status"
                  aria-live="polite"
                >
                  <Calendar className="w-3 h-3 inline mr-1" aria-hidden="true" />
                  INCLUDES POST SCHEDULING
                </div>
              )}
              {!isHighlighted && plan.popular && (
                <div className="absolute top-0 right-0 bg-gradient-to-r from-yellow-400 to-orange-500 text-black text-xs font-bold px-3 py-1 rounded-bl-lg">
                  MOST POPULAR
                </div>
              )}
              {plan.isContract && (
                <div className="absolute top-0 right-0 bg-gradient-to-r from-emerald-400 to-cyan-500 text-black text-xs font-bold px-3 py-1 rounded-bl-lg">
                  FREE TO JOIN
                </div>
              )}

              <CardContent className={`p-6 ${isHighlighted && highlightFeature === 'scheduling' ? 'pt-10' : ''}`}>
                <div className={`w-14 h-14 rounded-full bg-gradient-to-r ${plan.color} p-3 mb-4 flex items-center justify-center`}>
                  {plan.icon}
                </div>
                <h3 className="text-xl font-bold text-white mb-2">
                  {plan.name}
                </h3>
                {plan.description && (
                  <p className="text-sm text-gray-400 mb-3">{plan.description}</p>
                )}
                <div className="mb-4">
                  {plan.isContract || plan.isFree ? (
                    <span className={`text-4xl font-black bg-gradient-to-r ${plan.color} bg-clip-text text-transparent`}>
                      FREE
                    </span>
                  ) : (
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className={`text-4xl font-black bg-gradient-to-r ${plan.color} bg-clip-text text-transparent`}>
                        ${plan.price}
                      </span>
                      <span className="text-gray-400">/{plan.period || 'month'}</span>
                      {plan.monthlyEquivalent && (
                        <span className="text-yellow-400 text-sm font-medium">
                          that {plan.monthlyEquivalent} a month
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {plan.note && (
                  <div className="mb-4 p-2 bg-gray-500/10 border border-gray-500/30 rounded-lg">
                    <p className="text-xs text-gray-400 text-center font-medium flex items-center justify-center gap-1">
                      <Sparkles className="w-3 h-3" /> {plan.note}
                    </p>
                  </div>
                )}

                {plan.commissionNote && (
                  <div className="mb-4 p-2 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                    <p className="text-xs text-yellow-400 text-center font-medium">
                      ⚠️ {plan.commissionNote}
                    </p>
                  </div>
                )}

                <Button
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={subscribing || (plan.isContract && currentSubscription?.plan_id === 'contract')}
                  className={`w-full mb-4 bg-gradient-to-r ${plan.color} hover:opacity-90 text-white font-semibold py-2`}
                >
                  {subscribing ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin">⏳</span>
                      Processing...
                    </span>
                  ) : plan.isContract && currentSubscription?.plan_id === 'contract' ? (
                    '✓ Current Plan'
                  ) : plan.isContract ? (
                    'Join The Contract'
                  ) : (
                    'Subscribe Now'
                  )}
                </Button>

                <div className="space-y-2">
                  {plan.features.map((feature) => {
                    const isSchedulingFeature = feature.toLowerCase().includes('scheduling');
                    const shouldHighlightFeature = isSchedulingFeature && highlightFeature === 'scheduling';
                    
                    return (
                    <div 
                      key={`${plan.id}-${feature}`} 
                      className={`flex items-start gap-2 ${shouldHighlightFeature ? 'bg-yellow-400/20 -mx-2 px-2 py-1 rounded-lg' : ''}`}
                    >
                      {isSchedulingFeature ? (
                        <Calendar className={`w-4 h-4 flex-shrink-0 mt-0.5 ${shouldHighlightFeature ? 'text-yellow-400' : 'text-green-400'}`} aria-hidden="true" />
                      ) : (
                        <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
                      )}
                      <span className={`text-xs ${shouldHighlightFeature ? 'text-yellow-300 font-semibold' : 'text-gray-300'}`}>
                        {feature}
                        {shouldHighlightFeature && <span className="ml-1 text-yellow-400">✨</span>}
                      </span>
                    </div>
                    );
                  })}
                  
                  {/* Show scheduling note for plans without scheduling */}
                  {!plan.includesScheduling && !plan.isContract && highlightFeature === 'scheduling' && (
                    <div className="flex items-start gap-2 mt-3 pt-3 border-t border-gray-700">
                      <Calendar className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
                      <span className="text-xs text-gray-500">
                        Post scheduling available in Own Ya Royalties
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
            );
          })}
        </div>

        {/* Currency Support */}
        <div className="mt-12 text-center">
          <p className="text-gray-400 mb-4">Accepted payment methods worldwide</p>
          <div className="flex justify-center gap-6 flex-wrap">
            <Badge variant="outline" className="border-gray-600 text-gray-300 px-4 py-2">
              💳 Credit Card
            </Badge>
            <Badge variant="outline" className="border-gray-600 text-gray-300 px-4 py-2">
              🌍 Multi-Currency
            </Badge>
            <Badge variant="outline" className="border-gray-600 text-gray-300 px-4 py-2">
              🔒 Secure Payment
            </Badge>
            <Badge variant="outline" className="border-gray-600 text-gray-300 px-4 py-2">
              🔄 Cancel Anytime
            </Badge>
          </div>
        </div>

        {/* FAQ */}
        <div className="mt-16 max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-8">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            <Card className="bg-gray-900 border-gray-700">
              <CardContent className="pt-6">
                <h3 className="text-white font-semibold mb-2">
                  What currencies do you accept?
                </h3>
                <p className="text-gray-400 text-sm">
                  We accept all major currencies worldwide. Stripe automatically converts prices to your local currency.
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-gray-900 border-gray-700">
              <CardContent className="pt-6">
                <h3 className="text-white font-semibold mb-2">
                  Can I cancel anytime?
                </h3>
                <p className="text-gray-400 text-sm">
                  Yes! You can cancel your subscription at any time. You&apos;ll continue to have access until the end of your billing period.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gray-900 border-gray-700">
              <CardContent className="pt-6">
                <h3 className="text-white font-semibold mb-2">
                  How do I upgrade or downgrade my plan?
                </h3>
                <p className="text-gray-400 text-sm">
                  You can change your plan anytime from your account settings. Changes take effect immediately.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* CTA Footer */}
        <div className="max-w-4xl mx-auto mt-16 text-center">
          <p className="text-gray-400 mb-4">Ready to start creating?</p>
          <Button
            onClick={() => navigate(token ? '/browse' : '/login')}
            variant="outline"
            className="border-gray-600 hover:bg-gray-800 text-white"
          >
            {token ? 'Go to Dashboard' : 'Sign Up Free'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
